import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@trellixio/roaster-coffee';
import { TableSection, Pagination, Modal } from 'components';
import { useAPI, useDebounce, useGetUser } from 'hooks';
import { formatDate, formatNumber } from 'helpers';
import Locales from 'locales';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';

const InvoicesPage = () => {
  const { isAgency, isBank } = useGetUser();
  const { api } = useAPI();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openActivationModal, setOpenActivationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const debouncedSearchQuery = useDebounce(search, 1000);
  const queryClient = useQueryClient();
  const itemsPerPage = 5;
  const limit = 30;

  const fetchParams = search.length > 0 ? { q: debouncedSearchQuery } : { limit };

  const { data: invoices } = useQuery({
    queryFn: api.list('invoice/', fetchParams),
    queryKey: ['invoice/', debouncedSearchQuery, limit],
    keepPreviousData: true,
  });

  const totalPages = invoices ? Math.ceil(invoices.length / itemsPerPage) : 1;
  const startIndex = (page - 1) * itemsPerPage;
  const currentPageData = invoices ? invoices.slice(startIndex, startIndex + itemsPerPage) : [];

  const nextPage = () => setPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setPage((p) => Math.max(p - 1, 1));

  const statusVariant = (isPaid) =>
    isPaid ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300';

  const { mutate: deleteInvoice } = useMutation({
    mutationFn: (invoice) => api.remove('invoice', invoice.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      queryClient.invalidateQueries({ queryKey: ['deposit/'] });
      showToast({
        message: Locales.invoice.toast.delete_invoice,
        level: 'success',
      });
    },
  });

  const { mutate: activateInvoice } = useMutation({
    mutationFn: (invoice) => api.post(`invoice/${invoice.id}/activate/`),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/category/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/trip_category_paid/'] });
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      queryClient.invalidateQueries({ queryKey: ['category/'] });
      const { code } = data;
      showToast({
        message: Locales.formatString(Locales.invoice.toast.activate_invoice, {
          code: <strong>{code}</strong>,
        }),
        level: 'success',
      });
    },
  });

  const handleDelete = (invoice) => {
    setOpenDeleteModal(true);
    setSelectedItem(invoice);
  };

  const handleActivation = (invoice) => {
    setOpenActivationModal(true);
    setSelectedItem(invoice);
  };

  return (
    <TableSection
      title="Factures"
      description="Liste des paiements effectués par les sociétés"
      actionLabel="Ajouter un paiement"
      onSearch={setSearch}
      searchPlaceholder="Rechercher un paiement..."
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Référence</TableHead>
            <TableHead>Société</TableHead>
            <TableHead>Montant</TableHead>
            <TableHead>Dépôts</TableHead>
            <TableHead>Date de création</TableHead>
            <TableHead>Banque</TableHead>
            <TableHead>Date de paiement</TableHead>
            <TableHead>Statut</TableHead>
            {(isAgency || isBank) && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>

        <TableBody>
          {currentPageData.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell>{invoice.code}</TableCell>
              <TableCell>{invoice.agency_name}</TableCell>
              <TableCell> {`${formatNumber(invoice.amount)} F CFA`}</TableCell>
              <TableCell>{invoice.deposit_count}</TableCell>
              <TableCell>{formatDate(invoice.created)}</TableCell>
              <TableCell>{invoice.is_paid ? invoice.bank_name : '-'}</TableCell>
              <TableCell>{invoice.paid ? formatDate(invoice.paid) : '-'}</TableCell>
              <TableCell>
                <Badge variant="outline" className={`${statusVariant(invoice.is_paid)} rounded-full`}>
                  {invoice.is_paid ? 'Validé' : 'En attente'}
                </Badge>
              </TableCell>
              <TableCell>
                {isAgency && !invoice.is_paid && (
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(invoice)}>
                    Supprimer
                  </Button>
                )}

                {isBank && !invoice.is_paid && (
                  <Button
                    variant="secondary"
                    className="text-blue-600 hover:underline"
                    onClick={() => handleActivation(invoice)}
                  >
                    Confirmer
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {selectedItem && (
        <>
          {/* Delete */}
          <Modal
            isOpen={openDeleteModal}
            onOpenChange={setOpenDeleteModal}
            title={Locales.invoice.action_modal.title.delete}
            footer={
              <>
                <Button variant="ghost" onClick={() => setOpenDeleteModal(false)}>
                  {Locales.base.button.cancel}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    deleteInvoice(selectedItem);
                    setOpenDeleteModal(false);
                  }}
                >
                  {Locales.base.button.delete}
                </Button>
              </>
            }
          >
            <p>
              {Locales.formatString(Locales.invoice.delete_payment, {
                amount: <strong>{formatNumber(selectedItem.amount)}</strong>,
                code: <strong>{selectedItem.code}</strong>,
              })}
            </p>
          </Modal>

          {/* Activation */}
          <Modal
            isOpen={openActivationModal}
            onOpenChange={setOpenActivationModal}
            title={Locales.invoice.action_modal.title.validation}
            footer={
              <>
                <Button variant="ghost" onClick={() => setOpenActivationModal(false)}>
                  {Locales.base.button.cancel}
                </Button>
                <Button
                  variant="secondary"
                  className="text-blue-600 hover:underline"
                  onClick={() => {
                    activateInvoice(selectedItem);
                    setOpenActivationModal(false);
                  }}
                >
                  Confirmer
                </Button>
              </>
            }
          >
            <p>
              {Locales.formatString(Locales.invoice.confirm_payment, {
                amount: <strong>{formatNumber(selectedItem.amount)}</strong>,
                code: <strong>{selectedItem.code}</strong>,
              })}
            </p>
          </Modal>
        </>
      )}

      <Pagination page={page} totalPages={totalPages} onPrev={prevPage} onNext={nextPage} />
    </TableSection>
  );
};

export default InvoicesPage;
